"use client";

import { useState } from "react";
import { downloadRecoveryCodes } from "@/lib/security/recovery-codes-file";
import { useRouter } from "next/navigation";
import { PerksmithCounterLockup, Panel, SectionLabel, PrimaryAction, CounterButton } from "@/components/counter";
import { beginPosForcedEnrolment, completePosSecondFactor, confirmPosForcedEnrolment, establishPosSession, type PosCounter, type PosForcedEnrolment } from "@/lib/api/session";
import { PosApiError } from "@/lib/api/client";
import { dataSource } from "@/lib/data/source";

// Counter login / location picker. A POS session is scoped to a location (ADR-0015 D8): the cashier
// signs in with their credentials and picks which counter they're opening, which establishes the
// locationId-bearing PERKSMITH_AUTH session the edge requires.
//
// The credential login is proxied to the user-management service via /pos-auth/login, which
// validates the staff/location pair and sets the session cookie. Prod path = pos-orchestrator
// /pos/login edge route (ADR-0015).
//
// The fields start EMPTY. They used to be prefilled with a real working account and its password so
// a live checkout could be validated in one click — which put those credentials in the shipped
// bundle and left anybody who opened the POS one button away from a signed-in till.
export default function CounterPage() {
  const router = useRouter();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  /** Populated only once the credentials are accepted and this person works several counters. */
  const [counters, setCounters] = useState<PosCounter[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the password was right and a code is still owed. */
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaEnrolmentRequired, setMfaEnrolmentRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  /** The manual-entry key for a forced enrolment, set once `enroll-challenge` answers. */
  const [mfaEnrollment, setMfaEnrollment] = useState<PosForcedEnrolment | null>(null);
  /** The recovery codes from a forced enrolment, shown exactly once before routing on. */
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState<string[] | null>(null);
  /** Set when the till is OPEN and two-factor is still optional -- an offer, never a gate. */
  const [mfaOfferDaysLeft, setMfaOfferDaysLeft] = useState<number | null>(null);

  /**
   * Step two: the six digits, or a recovery code.
   *
   * <p>A counter may still have to be chosen afterwards, so this feeds the same outcome handling
   * the first step does rather than assuming the till is open.
   */
  async function submitSecondFactor(locationId?: string) {
    if (!mfaChallenge || !mfaCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await completePosSecondFactor(mfaChallenge, mfaCode.trim(), locationId);
      if (!result.opened && "counters" in result) {
        setCounters(result.counters);
        setBusy(false);
        return;
      }
      const next = new URLSearchParams(window.location.search).get("next");
      router.replace(next && next.startsWith("/") && !next.startsWith("//") ? next : "/");
    } catch (e) {
      setError(e instanceof PosApiError ? e.problem.message : "That code is not right. Try again.");
      setBusy(false);
    }
  }

  /**
   * Return them to where they were headed.
   *
   * The route gate records the destination as `?next=` when it sends an unauthenticated request
   * here, so a manager who followed a link to today's reports lands on reports rather than being
   * dropped at the counter home to find their way back. Only same-site paths are honoured — an
   * absolute URL in a query parameter is an open redirect, and this is a page people arrive at
   * from links.
   */
  function goToDestination() {
    const next = new URLSearchParams(window.location.search).get("next");
    router.replace(next && next.startsWith("/") && !next.startsWith("//") ? next : "/");
  }

  /**
   * Clears every state a challenged sign-in can leave behind, back to the credential form.
   *
   * Shared by the plain code challenge and the forced-enrolment steps, since both can be abandoned
   * from "Sign in as someone else".
   */
  function resetMfaFlow() {
    setMfaChallenge(null);
    setMfaEnrolmentRequired(false);
    setMfaEnrollment(null);
    setMfaRecoveryCodes(null);
    setMfaCode("");
    setError(null);
  }

  /**
   * Starts a forced enrolment on the challenge token, the moment sign-in says one is owed. Past the
   * deadline and un-enrolled, the till gets no session at all, so there is no back office to send a
   * cashier to; setup happens right here, on the challenge token in place of one.
   */
  async function beginForcedEnrolment(challengeToken: string) {
    setError(null);
    try {
      const enrolment = await beginPosForcedEnrolment(challengeToken);
      setMfaEnrollment(enrolment);
    } catch (e) {
      setError(e instanceof PosApiError ? e.problem.message : "Setup could not be started.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Confirms a forced enrolment: turns two-factor on, shows the recovery codes once, and finishes
   * the sign-in in the same step -- the till is left signed in, so there is no second sign-in.
   */
  async function submitForcedEnrolment() {
    if (!mfaChallenge || mfaCode.trim().length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const codes = await confirmPosForcedEnrolment(mfaChallenge, mfaCode.trim());
      setMfaRecoveryCodes(codes);
      setMfaEnrollment(null);
      setMfaCode("");
    } catch (e) {
      setError(e instanceof PosApiError ? e.problem.message : "That code is not right. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function openCounter(locationId?: string) {
    if (!identifier || !password) return;
    setBusy(true);
    setError(null);
    try {
      const result = await establishPosSession(identifier, password, locationId);
      if (!result.opened && "challengeToken" in result) {
        // Credentials accepted and the sign-in is NOT finished. No session cookie exists yet, so
        // this is a second step rather than a signed-in till waiting to be proven.
        setMfaChallenge(result.challengeToken);
        setMfaEnrolmentRequired(result.enrolmentRequired);
        if (result.enrolmentRequired) {
          await beginForcedEnrolment(result.challengeToken);
        } else {
          setBusy(false);
        }
        return;
      }
      if (!result.opened) {
        // Credentials accepted; this person works more than one counter. Ask which, and keep the
        // password in memory only for the second call.
        setCounters(result.counters);
        setBusy(false);
        return;
      }
      if (result.mfaOffer) {
        // The counter is OPEN and the cookie is set. This is a suggestion riding on a finished
        // sign-in, so skipping it is a local decision and needs nothing from the server.
        setMfaOfferDaysLeft(result.mfaOffer.daysLeft);
        setBusy(false);
        return;
      }
      goToDestination();
    } catch (e) {
      setError(e instanceof PosApiError ? e.problem.message : "Could not open the counter.");
      setBusy(false);
    }
  }

  return (
    <main className="relative grid min-h-dvh w-full grid-rows-[auto_1fr_auto] bg-bg-raised px-5 py-8">
      {/* Two slow blooms on the paper — see .counter-drift in globals.css for why
          this exists only on the sign-in and nowhere else in the till. */}
      <div className="counter-drift" aria-hidden="true">
        <span />
        <span />
      </div>

      {/*
       * The stationery's device (L-07): the ı and its tick blown up and cropped by
       * the corner, in the mark's own colours rather than a wash of them. Founder
       * call, and it is a step outside DESIGN.md twice over — Counter's only shadow
       * belongs to the overlay, and the mark is never given one. Kept honest by
       * staying bottom-right, well clear of the card, so nothing it sits under has
       * to fight it for contrast.
       */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <svg
          viewBox="0 0 64 74"
          fill="none"
          className="absolute bottom-[-14vmin] right-[-6vmin] w-[52vmin] max-w-none opacity-70 drop-shadow-device"
        >
          <path
            d="M22 14l8 8L48 4"
            className="stroke-honey-400"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="26" y="26" width="12" height="44" rx="3" className="fill-basalt-950" />
        </svg>
      </div>

      {/* Top left of the PAGE, not of the card: the lockup says whose machine this
          is, so it belongs to the screen rather than to the form on it. */}
      <div className="relative">
        <PerksmithCounterLockup size={56} />
      </div>

      {/* The card takes the middle row and centres in it, so a tall counter
          screen puts the fields at eye height rather than at the top edge. */}
      <div className="relative flex items-center justify-center py-7">

      {mfaOfferDaysLeft !== null ? (
        /*
         * An offer, not a step. The till is already open behind this panel -- Skip simply carries
         * on, which is why it is a full-width control rather than a link somebody has to find.
         */
        <Panel className="relative mx-auto w-full max-w-[460px] p-9 shadow-signin">
          <SectionLabel className="mb-1">Protect this account</SectionLabel>
          <p className="mb-3 text-sm text-text-weak">
            {mfaOfferDaysLeft === 1
              ? "Two-factor sign-in becomes required tomorrow."
              : `Two-factor sign-in becomes required in ${mfaOfferDaysLeft} days.`}{" "}
            Set it up in the back office from any browser, using an authenticator app on your phone.
          </p>
          <PrimaryAction
            className="mb-2 w-full"
            onClick={() => {
              const admin = process.env.NEXT_PUBLIC_ADMIN_APP_URL ?? "http://localhost:3000";
              window.location.href = `${admin}/settings/account`;
            }}
          >
            Set it up now
          </PrimaryAction>
          <CounterButton
            tone="secondary"
            block
            onClick={() => {
              setMfaOfferDaysLeft(null);
              goToDestination();
            }}
          >
            Skip for now
          </CounterButton>
        </Panel>
      ) : mfaRecoveryCodes ? (
        /*
         * The one moment these codes exist. A lost phone is the only self-service way back in once
         * two-factor is on, so this cannot be a toast that scrolls away -- it needs an explicit
         * acknowledgement before the counter opens.
         */
        <Panel className="relative mx-auto w-full max-w-[460px] p-9 shadow-signin">
          <SectionLabel className="mb-1">Save these recovery codes now</SectionLabel>
          <p className="mb-3 text-sm text-text-weak">
            Each one opens the counter once if you lose your phone. This is the only time they are
            shown.
          </p>
          <ul className="mb-3 grid grid-cols-2 gap-1.5 rounded-tile border border-stroke-strong bg-bg-base p-3 font-mono text-sm text-text-strong">
            {mfaRecoveryCodes.map((code) => (
              <li key={code}>{code}</li>
            ))}
          </ul>
          {/* Download before the acknowledgement, so "I have saved these" can be true. A till is
              a shared screen, so this is usually a file to print rather than one to keep here. */}
          <CounterButton
            tone="secondary"
            block
            className="mb-2"
            onClick={() => downloadRecoveryCodes(mfaRecoveryCodes, identifier)}
          >
            Download .txt
          </CounterButton>
          <PrimaryAction
            className="w-full"
            onClick={() => {
              setMfaRecoveryCodes(null);
              goToDestination();
            }}
          >
            I have saved these — open counter
          </PrimaryAction>
        </Panel>
      ) : mfaEnrollment ? (
        /*
         * Forced enrolment: past the deadline, un-enrolled, and no session -- there is no back
         * office to send a cashier to. Setup happens right here, on the challenge token. Entering
         * the code both turns two-factor on AND finishes the sign-in; there is no second sign-in.
         */
        <Panel className="relative mx-auto w-full max-w-[460px] p-9 shadow-signin">
          <SectionLabel className="mb-1">Set up two-factor sign-in</SectionLabel>
          <p className="mb-3 text-sm text-text-weak">
            Your role now requires a code from an authenticator app at every sign-in. Open your
            authenticator app (Google Authenticator, Authy, 1Password or similar), add an account,
            and enter this key when it asks for one:
          </p>
          <p className="mb-3 rounded-tile border border-stroke-strong bg-bg-base px-3 py-2 text-center font-ledger text-sm tracking-wide text-text-strong">
            {mfaEnrollment.secret}
          </p>
          <input
            aria-label="Six-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            value={mfaCode}
            onChange={(event) => setMfaCode(event.target.value)}
            placeholder="123456"
            className="mb-3 h-11 w-full rounded-tile border border-stroke-strong bg-bg-base px-3 text-sm text-text-strong"
          />
          {error && <p className="mb-3 text-sm text-text-error">{error}</p>}
          <PrimaryAction
            className="w-full"
            disabled={busy || mfaCode.trim().length !== 6}
            onClick={() => void submitForcedEnrolment()}
          >
            {busy ? "Checking…" : "Turn on and open counter"}
          </PrimaryAction>
          <CounterButton tone="quiet" size="dense" className="mt-3" onClick={resetMfaFlow}>
            Sign in as someone else
          </CounterButton>
        </Panel>
      ) : mfaChallenge ? (
        /*
         * Step two when a second factor is owed. It replaces the credential fields rather than
         * sitting under them: leaving those on screen invites a re-submit, which restarts the
         * sign-in and discards the challenge being held.
         */
        <Panel className="relative mx-auto w-full max-w-[460px] p-9 shadow-signin">
          <SectionLabel className="mb-1">
            {mfaEnrolmentRequired ? "Two-factor sign-in required" : "Authentication code"}
          </SectionLabel>
          <p className="mb-3 text-sm text-text-weak">
            {mfaEnrolmentRequired
              ? "Setting up two-factor sign-in, required for your role…"
              : "Enter the six-digit code from your authenticator app, or one of your recovery codes."}
          </p>
          {!mfaEnrolmentRequired ? (
            <>
              <input
                aria-label="Authentication code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value)}
                placeholder="123456"
                className="mb-3 h-11 w-full rounded-tile border border-stroke-strong bg-bg-base px-3 text-sm text-text-strong"
              />
              <PrimaryAction
                className="w-full"
                disabled={busy || !mfaCode.trim()}
                onClick={() => void submitSecondFactor()}
              >
                {busy ? "Checking…" : "Continue"}
              </PrimaryAction>
            </>
          ) : error ? (
            <PrimaryAction className="w-full" onClick={() => void beginForcedEnrolment(mfaChallenge)}>
              Try again
            </PrimaryAction>
          ) : null}
          {error && <p className="mt-3 text-sm text-text-error">{error}</p>}
          <CounterButton tone="quiet" size="dense" className="mt-3" onClick={resetMfaFlow}>
            Sign in as someone else
          </CounterButton>
        </Panel>
      ) : counters ? (
        /**
         * Step two, reached only when the server says this person works several counters. The
         * counters and their names come from that answer — nothing here is guessed, so every option
         * shown is one they can actually open.
         */
        <Panel className="relative mx-auto w-full max-w-[460px] p-9 shadow-signin">
          <h1 className="mb-1 font-display text-2xl font-bold tracking-[-0.02em] text-text-strong">
            Which counter?
          </h1>
          <p className="mb-6 font-text text-lg text-text-weak">
            You work more than one. Pick the till you are standing at.
          </p>
          <p className="mb-3 text-sm text-text-weak">
            Signed in as <span className="font-semibold text-text-strong">{identifier}</span>.
          </p>
          <ul className="flex flex-col gap-2">
            {counters.map((counter) => (
              <li key={counter.id}>
                <CounterButton
                  tone="secondary"
                  size="large"
                  block
                  disabled={busy}
                  onClick={() => void openCounter(counter.id)}
                  className="justify-between gap-3"
                >
                  <span className="font-text text-lg text-text-strong">{counter.name}</span>
                  <span className="font-mono text-sm tracking-[0.06em] text-text-weak">{counter.id}</span>
                </CounterButton>
              </li>
            ))}
          </ul>
          {error && <p className="mt-3 text-sm text-text-error">{error}</p>}
          <CounterButton
            tone="quiet"
            size="dense"
            className="mt-4"
            onClick={() => {
              setCounters(null);
              setPassword("");
              setError(null);
            }}
          >
            Sign in as someone else
          </CounterButton>
        </Panel>
      ) : (
      <Panel className="relative mx-auto w-full max-w-[460px] p-9 shadow-signin">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void openCounter();
          }}
        >
          <h1 className="mb-1 font-display text-2xl font-bold tracking-[-0.02em] text-text-strong">
            Open your counter
          </h1>
          <p className="mb-7 font-text text-lg text-text-weak">
            Sign in to start your shift{dataSource.live ? "" : " · mock mode"}.
          </p>

          {/* 56px fields: a cashier taps these with a thumb, standing, in a hurry. */}
          <div className="flex flex-col gap-5">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-text-weak">Email</span>
              <input
                type="text"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="min-h-target-lg w-full rounded-control border border-stroke-strong bg-bg-base px-3.5 text-base text-text-strong outline-none focus:border-stroke-brand placeholder:text-text-disabled"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-text-weak">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="min-h-target-lg w-full rounded-control border border-stroke-strong bg-bg-base px-3.5 text-base text-text-strong outline-none focus:border-stroke-brand placeholder:text-text-disabled"
              />
            </label>
          </div>

          {error && <p className="mt-4 text-sm text-text-error">{error}</p>}

          <div className="mt-6">
            <PrimaryAction type="submit">{busy ? "Signing in…" : "Continue"}</PrimaryAction>
          </div>

          <p className="mt-5 text-sm text-text-weak">
            Your session belongs to this counter for the length of the shift.
          </p>
        </form>
      </Panel>
      )}

      </div>

      <p className="relative text-center text-sm text-text-disabled">
        Trouble signing in? Ask the manager who set up this counter.
      </p>
    </main>
  );
}
